import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Values stay in English (canonical for the business inbox); labels are translated.
const SERVICE_OPTIONS = [
  { value: "In-Person Appointment Accompaniment (Orlando)", key: "contact.svc.accomp" },
  { value: "Virtual Appointment Support", key: "contact.svc.virtual" },
  { value: "Treatment Navigation", key: "contact.svc.nav" },
  { value: "Concierge Consultation", key: "contact.svc.concierge" },
  { value: "Medication & Protocol Education", key: "contact.svc.med" },
  { value: "Not sure yet", key: "contact.svc.unsure" },
];

export default function Contact() {
  const { t } = useT();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    service: "",
    preferred_format: "virtual",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/consultations`, form);
      setStatus("success");
      setForm({
        full_name: "",
        email: "",
        phone: "",
        service: "",
        preferred_format: "virtual",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(
        typeof detail === "string" ? detail : "Please check your inputs.",
      );
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#3F5043] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-[#F8F7F3]/70 font-semibold mb-4">
              {t("contact.eyebrow")}
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-[1.05] text-[#F8F7F3]">
              {t("contact.title.a")} <br />
              <em className="text-[#AE8F78]">{t("contact.title.em")}</em>
            </h2>
            <p className="mt-7 text-base sm:text-lg text-[#F8F7F3]/80 max-w-md leading-relaxed">
              {t("contact.intro.a")}
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-4">
                <MapPin
                  size={18}
                  className="text-[#AE8F78] mt-1 flex-shrink-0"
                />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#F8F7F3]/60 font-semibold mb-1">
                    {t("contact.label.area")}
                  </div>
                  <div className="text-[#F8F7F3]">
                    {t("contact.val.area")}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone
                  size={18}
                  className="text-[#AE8F78] mt-1 flex-shrink-0"
                />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#F8F7F3]/60 font-semibold mb-1">
                    {t("contact.label.call")}
                  </div>
                  <a
                    href="tel:+14073749834"
                    data-testid="contact-call"
                    className="text-[#F8F7F3] hover:text-[#AE8F78] transition-colors block"
                  >
                    (407) 374-9834
                  </a>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#F8F7F3]/60 font-semibold mt-3 mb-1">
                    {t("contact.label.text")}
                  </div>
                  <a
                    href="sms:+18446632125"
                    data-testid="contact-text"
                    className="text-[#F8F7F3] hover:text-[#AE8F78] transition-colors"
                  >
                    (844) 663-2125
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail
                  size={18}
                  className="text-[#AE8F78] mt-1 flex-shrink-0"
                />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-[#F8F7F3]/60 font-semibold mb-1">
                    {t("contact.label.email")}
                  </div>
                  <a
                href="mailto:contact@soarlife.consulting"
                data-testid="contact-email"
                className="text-[#F8F7F3] hover:text-[#AE8F78] transition-colors"
              >
                    contact@soarlife.consulting
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="bg-[#F8F7F3] rounded-[2rem] p-7 sm:p-10">
              {status === "success" ? (
                <div
                  data-testid="contact-success"
                  className="py-12 flex flex-col items-center text-center"
                >
                  <CheckCircle2
                    size={48}
                    className="text-[#899480] mb-5"
                    strokeWidth={1.2}
                  />
                  <h3 className="font-serif text-3xl text-[#3F5043] mb-3">
                    {t("contact.success.title")}
                  </h3>
                  <p className="text-[#3F5043]/75 max-w-md">
                    {t("contact.success.text")}
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    data-testid="contact-success-again"
                    className="mt-7 text-sm uppercase tracking-[0.22em] text-[#899480] hover:text-[#3F5043]"
                  >
                    {t("contact.success.again")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="contact-form"
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      label={t("contact.label.name")}
                      required
                      name="full_name"
                      value={form.full_name}
                      onChange={onChange}
                      testid="input-name"
                    />
                    <Field
                      label={t("contact.label.emailField")}
                      type="email"
                      required
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      testid="input-email"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field
                      label={t("contact.label.phone")}
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      testid="input-phone"
                    />
                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
                        {t("contact.label.service")}
                      </label>
                      <select
                        data-testid="input-service"
                        name="service"
                        value={form.service}
                        onChange={onChange}
                        className="w-full bg-transparent border-b border-[#BCB3A6] py-3 text-[#3F5043] focus:outline-none focus:border-[#3F5043] transition-colors"
                      >
                        <option value="">{t("contact.placeholder.service")}</option>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s.key} value={s.value}>
                            {t(s.key)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-3">
                      {t("contact.label.format")}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { v: "virtual", l: t("contact.format.virtual") },
                        { v: "in_person", l: t("contact.format.inperson") },
                        { v: "either", l: t("contact.format.either") },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          data-testid={`format-${opt.v}`}
                          onClick={() =>
                            setForm({ ...form, preferred_format: opt.v })
                          }
                          className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                            form.preferred_format === opt.v
                              ? "bg-[#3F5043] text-[#F8F7F3]"
                              : "bg-[#EDEDEE] text-[#3F5043] hover:bg-[#BCB3A6]/30"
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
                      {t("contact.label.message")}
                    </label>
                    <textarea
                      data-testid="input-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      placeholder={t("contact.placeholder.message")}
                      className="w-full bg-transparent border-b border-[#BCB3A6] py-3 text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:border-[#3F5043] transition-colors resize-none"
                    />
                  </div>

                  {status === "error" && (
                    <div
                      data-testid="contact-error"
                      className="text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-4 py-3"
                    >
                      {errorMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={status === "loading"}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-9 py-4 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#899480] transition-colors disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {t("contact.submitting")}
                      </>
                    ) : (
                      t("contact.submit")
                    )}
                  </button>

                  <p className="text-xs text-[#3F5043]/60 pt-2">
                    {t("contact.disclaimer")}
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, testid, ...rest }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
        {label}
        {required && <span className="text-[#875F45]"> *</span>}
      </label>
      <input
        data-testid={testid}
        required={required}
        className="w-full bg-transparent border-b border-[#BCB3A6] py-3 text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:border-[#3F5043] transition-colors"
        {...rest}
      />
    </div>
  );
}
